// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import 'core-js/stable';
// import cssVars from 'css-vars-ponyfill'
import 'regenerator-runtime/runtime'; // Fix issues when built in test mode.
// globalThis polyfill
import 'globalthis/polyfill';
import Vue from 'vue';
import App from './App.vue';
import router from './router/index.js';
import store from './store/index.js';
import VueLoading from 'vuejs-loading-plugin';
import NProgress from 'nprogress';
import * as Sentry from '@sentry/vue';
import '../node_modules/nprogress/nprogress.css';
import './shared/global-components/index.js';
import GotedoProgressLoader from './shared/spinners/GotedoProgressLoader.vue';
import 'animate.css';
import Notify from 'quasar/src/plugins/Notify.js';;
import GotedoActionConfirmationModalMixin from './shared/GotedoActionConfirmationModalMixin.js';
import globalVariablesMixin from './shared/globalVariablesMixin.js';
import GlobalFormMixin from './shared/GlobalFormMixin';
import './quasar';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import {
  detectUserLocaleFromStore,
  i18nMessages,
  setI18nLanguage,
  supportedLocales,
} from './i18n/index.js';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Settings } from 'luxon';
import emitterPlugin from './event_bus/index.js';
import { appReady } from './utils/app_readiness.js';

//
/**
 * CRITICAL: Reset app ready state
 *
 * This is used to track full-page reload and is important for
 * access control checks in the router file.
 */
appReady.set(false);

Vue.prototype.$NProgress = NProgress;

// Register the emitter plugin
// Call the emitter via `this.$emitter` in Vue components
Vue.use(emitterPlugin);

// Add listener as early as possible
i18next.on('initialized', () => {
  new Vue({
    el: '#app',
    router,
    template: '<App/>',
    store,
    components: {
      App,
    },
  });

  Notify.setDefaults({
    actions: [
      {
        // Use of `white` is important. Don't change.
        color: 'white',
        label: i18next.t('dismiss', { ns: 'general' }),
      },
    ],
    textColor: 'white',
    progress: false,
    position: 'top',
    classes: 'gotedo-notify',
    timeout: 5000,
  });
});

/**
 * @param {string} locale
 */
const onLanguageChange = (locale) => {
  Settings.defaultLocale = locale;
  window.localStorage.setItem('gotedo_locale', locale);
  // Important: update `Dismiss` button label
  Notify.setDefaults({
    actions: [
      {
        // Use of `white` is important. Don't change.
        color: 'white',
        label: i18next.t('dismiss', { ns: 'general' }),
      },
    ],
  });
};

i18next.on('languageChanged', onLanguageChange);

Vue.mixin(GotedoActionConfirmationModalMixin);
Vue.mixin(globalVariablesMixin);
Vue.mixin(GlobalFormMixin);

Vue.use(VueLoading, {
  dark: true,
  text: 'Processing...',
  loading: false,
  background: 'rgba(255,255,255,.85)',
  customLoader: GotedoProgressLoader, // replaces the spinner and text with your own
  //classes: ["myclass"] // array, object or string
});

/* Vue.config.errorHandler = function (err, vm, info) {
  console.error(`Error: ${err.toString()}\nComponent: ${vm.$options.name}\nInfo: ${info}`);
}; */

/*  eslint-disable no-unused-vars */
router.beforeEach((to, from, next) => {
  if (to.name) {
    NProgress.set(0.4);
  }
  next();
});

router.afterEach((to, from, next) => {
  NProgress.done();
});
/*  eslint-disable no-unused-vars */

Sentry.init({
  Vue,
  dsn: 'https://df9bbea4970e4c7fb39d55c77c64ce4a@o1084930.ingest.sentry.io/6095196',
  enabled: process.env.NODE_ENV === 'production',
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', 'app.gotedo.com', /^\//],
      beforeNavigate: (context) => {
        return {
          ...context,
          // You could use your UI's routing library to find the matching
          // route template here. We don't have one right now, so do some basic
          // parameter replacements.
          name: router.currentRoute.path,
        };
      },
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  lookup() {
    return detectUserLocaleFromStore();
  },
  name: 'StoreLocaleDetector',
  cacheUserLanguage(lang) {
    setI18nLanguage(lang);
  },
});

Vue.use(I18NextVue, {
  i18next,
  rerenderOn: ['initialized', 'languageChanged', 'loaded', 'added', 'removed'],
});

// CRITICAL: `i18next` must be initialised in this `main` file
// else `i18next` won't work on some pages when window is refreshed
i18next.use(LanguageDetector).init({
  resources: i18nMessages,
  ns: [
    'general',
    'alerts',
    'forms',
    'toasts',
    'modals',
    'onboarding_data_migration_module',
    'gotedo_table_definitions',
    'router_file',
  ],
  defaultNS: 'general',
  fallbackLng: 'en',
  supportedLngs: supportedLocales.map((locale) => locale.code),
  debug: process.env.NODE_ENV === 'development',
  interpolation: {
    // Turn off string escape globally as backend sanitises data
    escapeValue: false,
  },
});
